import React, { useEffect } from 'react';
import { MediaQueryProvider } from 'react-media-query-hoc';
import getConfig from 'next/config';
import { gql, useQuery } from '@apollo/client';
import { tokens } from '@domain-group/fe-brary';
import { ReviewsProvider } from '../recommendations-enquiry-section';

import {
  AgencyProfileMapWithFragment,
  AppraisalCTAWithFragment,
  ContactAboutSectionWithFragment,
  ContactCurrentListings,
  ContactEnquiryFormWithFragment,
  ContactHeroWithFragment,
  ContactListingsStoryWithFragment,
  ContactProfileFooterWithFragment,
  ContactProfileHeaderWithFragment,
  ContactRecommendationsWithFragment,
  ContactSalesStatisticsStoryWithFragment,
  ContactSalesStatisticsWithFragment,
  ContactTestimonialsWithFragment,
  ContactTopSummaryWithFragment,
  ContactVideoWithFragment,
  InView,
  LoadingPage,
  SectionWrapper,
} from '..';
import { ShortlistProvider } from '../../contexts';

import { COMPONENT_IDS, AGENCY_AGENT_LAYOUT_MAX_WIDTH } from '../../constants';
import {
  trackContactAboutSection,
  trackContactAppraisalCta,
  trackContactEnquiryForm,
  trackContactHero,
  trackCurrentListings,
  trackProfilePage,
  trackSalesStats,
  trackContactRecommendations,
} from '../../tracking';
import { getAppraisalCTAUrl } from '../../utils';

import * as styles from '../../styles/trade-profile.style';
import { AgentRecommendations } from '../../pages/real-estate-agent/recommendations';
import { AgentRecommendationsWithStarRating } from '../../pages/real-estate-agent/recommendations-with-star-ratings';

const { publicRuntimeConfig } = getConfig();

// jobCategory and statistics.averageDaysOnMarket/averageSoldPrice below
// aren't used by this (legacy) component. They remain here for the legacy
// profile, while the 2026 profile owns its query and SSR prepass.
export const AGENT_PROFILE_QUERY = gql`
  query getContactProfile(
    $contactId: Int!
    $imageFormat: ImageFormat
    $quality: Int
  ) {
    contactByContactId(contactId: $contactId) {
      id
      agentIdV2
      agencyId
      name
      profileUrl
      profileTier
      depthTier
      jobCategory
      jobTitle
      statistics {
        averageDaysOnMarket
        averageSoldPrice
      }
      recommendations {
        numberOfReviews
        overallStarRating
      }

      ...${ContactProfileFooterWithFragment.fragmentName}
      ...${ContactProfileHeaderWithFragment.fragmentName}
      ...${ContactHeroWithFragment.fragmentName}
      ...${ContactTopSummaryWithFragment.fragmentName}
      ...${ContactAboutSectionWithFragment.fragmentName}
      ...${ContactSalesStatisticsStoryWithFragment.fragmentName}
      ...${ContactSalesStatisticsWithFragment.fragmentName}
      ...${AppraisalCTAWithFragment.fragmentName}
      ...${ContactListingsStoryWithFragment.fragmentName}
      ...${ContactRecommendationsWithFragment.fragmentName}
      ...${ContactTestimonialsWithFragment.fragmentName}
      ...${ContactEnquiryFormWithFragment.fragmentName}
      ...${ContactVideoWithFragment.fragmentName}

      agencyByAgencyId {
        id
        name
        profileUrl
        address {
          displayAddress
          state
          suburb {
            id
            suburbId
            name
          }
          postcode
        }

        ...${AgencyProfileMapWithFragment.fragmentName}
      }
    }
  }
  ${ContactProfileFooterWithFragment.fragment}
  ${ContactProfileHeaderWithFragment.fragment}
  ${ContactHeroWithFragment.fragment}
  ${ContactTopSummaryWithFragment.fragment}
  ${ContactAboutSectionWithFragment.fragment}
  ${ContactSalesStatisticsStoryWithFragment.fragment}
  ${ContactSalesStatisticsWithFragment.fragment}
  ${AppraisalCTAWithFragment.fragment}
  ${ContactListingsStoryWithFragment.fragment}
  ${ContactRecommendationsWithFragment.fragment}
  ${ContactTestimonialsWithFragment.fragment}
  ${ContactEnquiryFormWithFragment.fragment}
  ${ContactVideoWithFragment.fragment}
  ${AgencyProfileMapWithFragment.fragment}
`;

export interface AgentProfileProps {
  contactId: number;
  isEmbeddedApp?: boolean;
  requestAppraisalUrl: string;
  hideAppraisalCta?: boolean;
  mixpanelHasInitialized: boolean;
  starRatingsEnabled?: boolean;
  hideStarRatings?: boolean;
  imageFormat?: string;
  quality?: number;
}

const AgentProfile = ({
  isEmbeddedApp = false,
  contactId,
  requestAppraisalUrl,
  hideAppraisalCta = false,
  mixpanelHasInitialized = false,
  starRatingsEnabled = false,
  hideStarRatings = false,
  imageFormat,
  quality,
}: AgentProfileProps): JSX.Element => {
  const { data, loading, error } = useQuery(AGENT_PROFILE_QUERY, {
    variables: { contactId, imageFormat, quality },
    errorPolicy: 'all', // https://www.apollographql.com/docs/react/v2/data/error-handling/#error-policies
  });

  const { agentIdV2, agencyId, hasEmail } = data?.contactByContactId || {};

  const shouldHideAppraisalCta = hideAppraisalCta || !hasEmail;

  useEffect(() => {
    if (mixpanelHasInitialized && data?.contactByContactId) {
      trackProfilePage.trackPageViewAgentProfile({
        locationId:
          data?.contactByContactId?.agencyByAgencyId?.address?.suburb?.slug,
      });
    }
  }, [mixpanelHasInitialized, data?.contactByContactId, contactId]);

  if (error) {
    throw new Error(`Something went wrong, ${error}`);
  }

  if (loading) {
    return <LoadingPage />;
  }

  if (!data || !agentIdV2 || !agencyId) {
    throw new Error('Insufficient data to render Agent Profile page.');
  }

  const hasReviews =
    // just to guard when there are reviews but numberOfReviews from RMA is somehow undefined
    data?.contactByContactId?.recommendations?.numberOfReviews !== undefined &&
    Boolean(data?.contactByContactId?.recommendations?.reviews?.length);

  const showRating = starRatingsEnabled && hasReviews;
  const hasOverallStarRating = Boolean(
    data?.contactByContactId?.recommendations?.overallStarRating,
  );
  const hasTotalReviews = Boolean(
    data?.contactByContactId?.recommendations?.numberOfReviews,
  );
  const overallStarRating =
    showRating && hasOverallStarRating && hasTotalReviews && !hideStarRatings
      ? {
          rating: data?.contactByContactId?.recommendations?.overallStarRating,
          totalReviews:
            data?.contactByContactId?.recommendations?.numberOfReviews,
        }
      : undefined;

  return (
    <>
      <div
        css={styles.page}
        data-testid="trade-profile"
        data-version={publicRuntimeConfig.version}
      >
        <MediaQueryProvider>
          <ShortlistProvider>
            <ReviewsProvider>
              <ContactHeroWithFragment
                agentIdV2={agentIdV2}
                agencyId={agencyId}
                renderComponent={
                  isEmbeddedApp ? null : (
                    <ContactProfileHeaderWithFragment
                      agentIdV2={agentIdV2}
                      agencyId={agencyId}
                    />
                  )
                }
                emailFormComponentId={COMPONENT_IDS.enquiryForm}
                onAgencyAddressClick={trackContactHero.agencyAddressClick}
                onAgencyLogoClick={trackContactHero.agencyLogoClick}
                onCallButtonClick={() => {
                  trackContactHero.callButtonClick({
                    locationId:
                      data?.contactByContactId?.agencyByAgencyId?.address
                        ?.suburb?.slug,
                  });
                }}
                onEmailButtonClick={trackContactHero.emailButtonClick}
                onSocialClick={() => trackContactHero.trackSocialLink}
                onVendorTotalReviewsClick={
                  trackContactRecommendations.trackContactTotalReviews
                }
                desktopWidth={AGENCY_AGENT_LAYOUT_MAX_WIDTH}
                overallStarRating={overallStarRating}
                imageFormat={imageFormat}
              />
              <SectionWrapper isShallow componentId={COMPONENT_IDS.summary}>
                <ContactTopSummaryWithFragment
                  agentIdV2={agentIdV2}
                  agencyId={agencyId}
                />
              </SectionWrapper>
              <SectionWrapper
                isNarrow
                isShallow
                componentId={COMPONENT_IDS.about}
              >
                <ContactAboutSectionWithFragment
                  agentIdV2={agentIdV2}
                  agencyId={agencyId}
                  onReadMore={trackContactAboutSection.trackReadMore}
                  imageFormat={imageFormat}
                  quality={quality}
                />
              </SectionWrapper>
              <SectionWrapper componentId={COMPONENT_IDS.statistics}>
                <ContactSalesStatisticsStoryWithFragment
                  agentIdV2={agentIdV2}
                  agencyId={agencyId}
                />
                <ContactSalesStatisticsWithFragment
                  agentIdV2={agentIdV2}
                  agencyId={agencyId}
                  tracking={{
                    onSuburbChange: trackSalesStats.contact.onSelectSuburb,
                  }}
                />
              </SectionWrapper>
              {!shouldHideAppraisalCta && (
                <SectionWrapper componentId="profile-appraisal-cta">
                  <AppraisalCTAWithFragment
                    contactId={contactId}
                    agentIdV2={agentIdV2}
                    agencyId={agencyId}
                    url={getAppraisalCTAUrl({
                      url: requestAppraisalUrl,
                      agentId: `${contactId}`,
                      entryPoint: 'Agent Profile',
                    })}
                    overallStarRating={overallStarRating}
                    onClick={() => {
                      trackContactAppraisalCta.trackOnClick({
                        locationId:
                          data?.contactByContactId?.agencyByAgencyId?.address
                            ?.suburb?.slug,
                      });
                    }}
                    onVendorTotalReviewsClick={
                      trackContactRecommendations.trackContactTotalReviews
                    }
                    imageFormat={imageFormat}
                  />
                </SectionWrapper>
              )}
              <SectionWrapper componentId={COMPONENT_IDS.listings}>
                <ContactListingsStoryWithFragment
                  agentIdV2={agentIdV2}
                  agencyId={agencyId}
                />
                <ContactCurrentListings
                  contactId={contactId}
                  tracking={{
                    onSelectListingStatus:
                      trackCurrentListings.contact.onSelectListingStatus,
                    onSelectSelectSortBy: trackCurrentListings.contact.onSortBy,
                    onSelectPropertyType:
                      trackCurrentListings.contact.onPropertyTypeChange,
                    onSelectSuburb: trackCurrentListings.contact.onSuburbChange,
                    onSelectBedrooms:
                      trackCurrentListings.contact.onBedroomChange,
                    onPageChange: trackCurrentListings.contact.onViewMore,
                    onListingCardClick:
                      trackCurrentListings.contact.onListingCardClick,
                  }}
                  mobilePadding={tokens.spacing.m} // Removes the margin for the picker on mobile
                  imageFormat={imageFormat}
                  quality={quality}
                />
              </SectionWrapper>
              <SectionWrapper componentId={COMPONENT_IDS.recommendations}>
                {showRating ? (
                  <AgentRecommendationsWithStarRating
                    agentIdV2={agentIdV2}
                    agencyId={agencyId}
                    contactId={contactId}
                    profileTier={data?.contactByContactId?.profileTier}
                    title={
                      data?.contactByContactId?.firstName
                        ? `${data?.contactByContactId?.firstName}'s`
                        : undefined
                    }
                    overallStarRating={overallStarRating}
                    imageFormat={imageFormat}
                  />
                ) : (
                  <AgentRecommendations
                    agentIdV2={agentIdV2}
                    agencyId={agencyId}
                  />
                )}
              </SectionWrapper>
              <SectionWrapper componentId={COMPONENT_IDS.testimonials}>
                <ContactTestimonialsWithFragment
                  agentIdV2={agentIdV2}
                  agencyId={agencyId}
                />
              </SectionWrapper>
              <SectionWrapper componentId={COMPONENT_IDS.video}>
                <ContactVideoWithFragment
                  agentIdV2={agentIdV2}
                  agencyId={agencyId}
                />
              </SectionWrapper>
              <SectionWrapper
                componentId={COMPONENT_IDS.enquiryForm}
                bottomBorder={false}
              >
                {!showRating && (
                  <InView
                    onActivate={trackContactEnquiryForm.trackEnquiryFormViewed}
                  >
                    <ContactEnquiryFormWithFragment
                      agentIdV2={agentIdV2}
                      agencyId={agencyId}
                      contactId={contactId}
                      onSelectIntent={trackContactEnquiryForm.trackSelectIntent}
                      onSubmitIntent={trackContactEnquiryForm.trackSubmitIntent}
                      onSubmit={trackContactEnquiryForm.trackSubmit}
                      onSubmitSuccess={
                        trackContactEnquiryForm.trackSubmitSuccess
                      }
                      onSubmitError={trackContactEnquiryForm.trackSubmitError}
                      imageFormat={imageFormat}
                    />
                  </InView>
                )}
              </SectionWrapper>
              <SectionWrapper noStyles componentId={COMPONENT_IDS.map}>
                <AgencyProfileMapWithFragment
                  agencyId={agencyId}
                  imageFormat={imageFormat}
                />
              </SectionWrapper>
            </ReviewsProvider>
          </ShortlistProvider>
        </MediaQueryProvider>
      </div>

      {isEmbeddedApp ? null : (
        <ContactProfileFooterWithFragment
          agentIdV2={agentIdV2}
          agencyId={agencyId}
        />
      )}
    </>
  );
};

export default AgentProfile;
